import React from 'react'
import { api } from '../../lib/api'
import {
  CARD_NEWS_SOURCE_MONTHLY_BRIEF,
  CARD_NEWS_SOURCE_TEMPLATE,
  mergeMonthlyIssueContent,
  parseMonthlyIssuePeriodLabel,
} from '../../lib/monthlyIssueLanding'
import { normalizePersonalLandingSections } from './normalize'

/**
 * 공개 소개페이지와 동일 규칙으로 이달의 이슈(카드뉴스) 데이터를 준비한다. 편집 미리보기용.
 * @param {Record<string, unknown>|null|undefined} data — `/users/me/public-page` 형태 + 로컬 초안 필드
 */
export function usePersonalLandingMonthlyIssue(data) {
  const [monthlyIssue, setMonthlyIssue] = React.useState({
    loading: false,
    brief: null,
    images: [],
    source: CARD_NEWS_SOURCE_MONTHLY_BRIEF,
    periodLabel: null,
  })

  React.useEffect(() => {
    let ignore = false
    const empty = () => ({
      loading: false,
      brief: null,
      images: [],
      source: CARD_NEWS_SOURCE_MONTHLY_BRIEF,
      periodLabel: null,
    })

    async function loadLatestMonthlyIssue() {
      if (!data?.landingSections || !Array.isArray(data.landingSections)) {
        if (!ignore) setMonthlyIssue(empty())
        return
      }
      const sections = normalizePersonalLandingSections(data.landingSections, data, data?.landingPageStyle)
      const miSection = sections.find((section) => section?.type === 'monthly-issue' && section?.enabled !== false)
      if (!miSection) {
        if (!ignore) setMonthlyIssue(empty())
        return
      }
      const cfg = mergeMonthlyIssueContent(miSection.content)

      if (cfg.cardNewsSource === CARD_NEWS_SOURCE_TEMPLATE && cfg.cardNewsTemplateEmbed?.images?.length) {
        const embed = cfg.cardNewsTemplateEmbed
        const images = embed.images.filter((it) => it?.fileId)
        const { year: py, month: pm } = parseMonthlyIssuePeriodLabel(embed.periodLabel)
        if (!ignore) {
          setMonthlyIssue({
            loading: false,
            brief: {
              year: py || null,
              month: pm || null,
              payload: embed.payload || null,
            },
            images,
            source: CARD_NEWS_SOURCE_TEMPLATE,
            periodLabel: embed.periodLabel || null,
          })
        }
        return
      }

      if (!ignore) setMonthlyIssue((prev) => ({ ...prev, loading: true }))
      try {
        const checkpoints = []
        const now = new Date()
        for (let i = 0; i < 15; i += 1) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
          checkpoints.push({ year: d.getFullYear(), month: d.getMonth() + 1 })
        }

        let foundBrief = null
        let foundImages = []
        let briefWithoutImages = null
        for (const point of checkpoints) {
          const briefRes = await api.get('/insurer-newsletter-monthly-briefs/public', {
            params: { year: point.year, month: point.month },
            __silent: true,
          })
          const briefPayload = (briefRes?.data && briefRes.data.data !== undefined) ? briefRes.data.data : briefRes.data
          if (!briefPayload?.id) continue

          const imgRes = await api.get(`/insurer-newsletter-monthly-briefs/${briefPayload.id}/card-images/public`, { __silent: true })
          const imgPayload = (imgRes?.data && imgRes.data.data !== undefined) ? imgRes.data.data : imgRes.data
          const images = Array.isArray(imgPayload) ? imgPayload.filter((item) => item?.fileId) : []
          if (images.length > 0) {
            foundBrief = briefPayload
            foundImages = images
            break
          }
          if (!briefWithoutImages) {
            briefWithoutImages = briefPayload
          }
        }

        if (!foundBrief && briefWithoutImages) {
          foundBrief = briefWithoutImages
          foundImages = []
        }

        if (!ignore) {
          setMonthlyIssue({
            loading: false,
            brief: foundBrief,
            images: foundImages,
            source: CARD_NEWS_SOURCE_MONTHLY_BRIEF,
            periodLabel:
              foundBrief?.year && foundBrief?.month
                ? `${foundBrief.year}.${String(foundBrief.month).padStart(2, '0')}`
                : null,
          })
        }
      } catch {
        if (!ignore) setMonthlyIssue(empty())
      }
    }

    loadLatestMonthlyIssue()
    return () => { ignore = true }
  }, [data])

  return monthlyIssue
}
